import { AdminShell, validateSettingsSearch } from "@admin/AdminShell";
import { useSessionKey } from "@auth/sessionStore";
import { ClinicBoardPage } from "@board/ClinicBoardPage";
import { CalendarShell } from "@calendar/CalendarShell";
import { validateScheduleSearch } from "@calendar/scheduleSearch";
import { WriteErrorNotice } from "@components/WriteErrorNotice";
// Datadog's wrapper names each RUM view after the route pattern, not the raw URL.
import { createRouter } from "@datadog/browser-rum-react/tanstack-router";
import { EmailRoute } from "@email/EmailRoute";
import { validateEmailSearch } from "@email/emailSearch";
import { IncomingEmailNotice } from "@email/IncomingEmailNotice";
import { FrontDeskDisplay, FrontDeskHome } from "@frontdesk/FrontDeskDisplay";
import { IncomingCallCards } from "@notifications/IncomingCallCard";
import { PatientPage } from "@patients/PatientPage";
import { PhoneRoute, validateCallsSearch } from "@phone/PhoneRoute";
import { TranscriptionProvider } from "@providers/TranscriptionProvider";
import { VoiceSessionProvider } from "@providers/VoiceSessionProvider";
import { cn } from "@scribenote/brian-design-system";
import { FrontDeskLayout } from "@shell/FrontDeskLayout";
import { WorkstationLayout } from "@shell/WorkstationLayout";
import {
  createRootRoute,
  createRoute,
  getRouteApi,
  Navigate,
  Outlet,
  RouterProvider,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { TodosPage } from "@todos/TodosPage";
import { StaffPhotoPage } from "@users/StaffPhotoPage";
import { isFrontDeskUser } from "@users/userClient";
import { useCurrentUser } from "@users/userQueries";
import { NewBuildNotice } from "@version/NewBuildNotice";
import { VoiceprintPage } from "@voiceprint/VoiceprintPage";
import { lazy, Suspense } from "react";

// Must stay a literal import.meta.env condition around a dynamic import so Rollup drops the chunk from non-dev builds.
const DevInjectPanel =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_TOOLS === "true"
    ? lazy(() =>
        import("@components/DevInjectPanel").then((m) => ({
          default: m.DevInjectPanel,
        })),
      )
    : null;

// Talk-to-Brian providers live here so navigating between shells doesn't interrupt a recording or voice session.
const RootLayout = () => {
  // Notices clear the displays' bottom phone dock.
  const onDisplay = useRouterState({
    select: (s) => s.location.pathname.startsWith("/display"),
  });
  return (
    <TranscriptionProvider>
      <VoiceSessionProvider>
        <Outlet />
        <div
          className={cn(
            "z-toast fixed right-4 flex flex-col items-end gap-3 sm:right-6",
            onDisplay
              ? "bottom-44 [--call-popup-space:12rem]"
              : "bottom-24 [--call-popup-space:7rem]",
          )}
        >
          <IncomingEmailNotice />
          <IncomingCallCards />
          <WriteErrorNotice />
          <NewBuildNotice />
        </div>
        {DevInjectPanel && (
          <Suspense fallback={null}>
            <DevInjectPanel />
          </Suspense>
        )}
      </VoiceSessionProvider>
    </TranscriptionProvider>
  );
};

const workstationSchedule = getRouteApi("/workstation/schedule");
const frontDeskSchedule = getRouteApi("/display/front-desk/schedule");
const workstationPatient = getRouteApi("/workstation/patients/$patientId");
const frontDeskPatient = getRouteApi("/display/front-desk/patients/$patientId");
const workstationCalls = getRouteApi("/workstation/calls");
const frontDeskCalls = getRouteApi("/display/front-desk/calls");

const WorkstationSchedule = () => {
  const navigate = useNavigate();
  return (
    <CalendarShell
      search={workstationSchedule.useSearch()}
      onSearchChange={(search) => void navigate({ to: "/schedule", search })}
    />
  );
};

const FrontDeskSchedule = () => {
  const navigate = useNavigate();
  return (
    <CalendarShell
      search={frontDeskSchedule.useSearch()}
      onSearchChange={(search) =>
        void navigate({ to: "/display/front-desk/schedule", search })
      }
    />
  );
};

const workstationEmail = getRouteApi("/workstation/email");
const frontDeskEmail = getRouteApi("/display/front-desk/email");

const WorkstationEmail = () => (
  <EmailRoute search={workstationEmail.useSearch()} />
);

const FrontDeskEmail = () => <EmailRoute search={frontDeskEmail.useSearch()} />;

const WorkstationPatient = () => (
  <PatientPage patientId={workstationPatient.useParams().patientId} />
);

const FrontDeskPatient = () => (
  <PatientPage patientId={frontDeskPatient.useParams().patientId} />
);

const WorkstationCalls = () => (
  <PhoneRoute search={workstationCalls.useSearch()} />
);

const FrontDeskCalls = () => <PhoneRoute search={frontDeskCalls.useSearch()} />;

const rootRoute = createRootRoute({ component: RootLayout });

// Waits for the user so reception never flashes the workstation after sign-in.
const HomeRedirect = () => {
  const { data: user, isPending } = useCurrentUser(useSessionKey());
  if (isPending) return null;
  return (
    <Navigate
      to={user && isFrontDeskUser(user) ? "/display/front-desk" : "/board"}
      replace
    />
  );
};

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRedirect,
});

const workstationRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "workstation",
  component: WorkstationLayout,
});

const boardRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/board",
  component: ClinicBoardPage,
});

const todosRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/todos",
  component: TodosPage,
});

const scheduleRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/schedule",
  validateSearch: validateScheduleSearch,
  component: WorkstationSchedule,
});

const callsRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/calls",
  validateSearch: validateCallsSearch,
  component: WorkstationCalls,
});

const emailRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/email",
  validateSearch: validateEmailSearch,
  component: WorkstationEmail,
});

const patientRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/patients/$patientId",
  component: WorkstationPatient,
});

const settingsRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/settings",
  validateSearch: validateSettingsSearch,
  component: AdminShell,
});

const voiceprintRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/voiceprint",
  component: VoiceprintPage,
});

const referencePhotoRoute = createRoute({
  getParentRoute: () => workstationRoute,
  path: "/reference-photo",
  component: StaffPhotoPage,
});

const displayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/display",
  component: FrontDeskLayout,
});

// A browser left at /display (a screen once) that a staff member signs in on goes home.
const displayIndexRoute = createRoute({
  getParentRoute: () => displayRoute,
  path: "/",
  component: () => <Navigate to="/" replace />,
});

const frontDeskDisplayRoute = createRoute({
  getParentRoute: () => displayRoute,
  path: "/front-desk",
  component: FrontDeskDisplay,
});

const frontDeskHomeRoute = createRoute({
  getParentRoute: () => frontDeskDisplayRoute,
  path: "/",
  component: FrontDeskHome,
});

const frontDeskScheduleRoute = createRoute({
  getParentRoute: () => frontDeskDisplayRoute,
  path: "/schedule",
  validateSearch: validateScheduleSearch,
  component: FrontDeskSchedule,
});

const frontDeskCallsRoute = createRoute({
  getParentRoute: () => frontDeskDisplayRoute,
  path: "/calls",
  validateSearch: validateCallsSearch,
  component: FrontDeskCalls,
});

const frontDeskEmailRoute = createRoute({
  getParentRoute: () => frontDeskDisplayRoute,
  path: "/email",
  validateSearch: validateEmailSearch,
  component: FrontDeskEmail,
});

const frontDeskPatientRoute = createRoute({
  getParentRoute: () => frontDeskDisplayRoute,
  path: "/patients/$patientId",
  component: FrontDeskPatient,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  workstationRoute.addChildren([
    boardRoute,
    todosRoute,
    scheduleRoute,
    callsRoute,
    emailRoute,
    patientRoute,
    settingsRoute,
    voiceprintRoute,
    referencePhotoRoute,
  ]),
  displayRoute.addChildren([
    displayIndexRoute,
    frontDeskDisplayRoute.addChildren([
      frontDeskHomeRoute,
      frontDeskScheduleRoute,
      frontDeskCallsRoute,
      frontDeskEmailRoute,
      frontDeskPatientRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => <Navigate to="/" />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export const AppShell = () => <RouterProvider router={router} />;
